import {observer} from "mobx-react";
import EditorPageLayout from "../../../../../../components/layouts/editor-page/editor-page-layout";
import useTranslation from "../../../../../../translation/hooks/translation-hook";
import {useCallback, useState} from "react";
import {ConfirmModal} from "../../../../../../components/modal/components/confirm-modal/confirm-modal";
import useNavigationBlocker from "../../../../../../hooks/navigation-blocker-hook";
import TranslationInput from "../../../../../../translation/translation-input/translation-input";
import ListLayout from "../../../../../../components/layouts/list-layout/list-layout";
import {ADDRESS_SLIP_TRANSLATION_KEYS} from "./constants";
import addressSlip from "../../../../../../images/previews/address-slip.svg";
import {FormFieldLine} from "../../../../../../components/form-field-line/form-field-line";
import dashboardSettingsStore from "../../../../../../stores/dashboard-settings-store";
import {PROFILE_CAPABILITIES} from "../../../../../../models/ProfileCapabilities";


const AddressSlipTranslationsEditorPage: React.FC = observer(() => {
    const [isShowUnsaved, setIsShowUnsaved] = useState(false);

    const addressSlipCapability = dashboardSettingsStore.resolveCapability(
        PROFILE_CAPABILITIES.RETURN_METHOD_ADDRESS_SLIP_SETTINGS
    );

    const {
        getHint,
        hasChanges: hasTranslationChanges,
        discardChanges: translationsDiscardChanges,
        save: saveTranslations,
    } = useTranslation();

    const hasChanges = () => {
        return hasTranslationChanges();
    }

    const {retryNavigation, unblockNavigation} = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    const discardChanges = () => {
        translationsDiscardChanges();
    }

    const onSaveClicked = () => {
        const promised = [];

        if (hasTranslationChanges()) {
            promised.push(
                saveTranslations()
            );
        }

        Promise.all(promised).then(() => {
            unblockNavigation();
        })
    }

    return <>
        <EditorPageLayout
            toolbar={{
                saveButton: {
                    hasChanges: hasChanges(),
                    disabled: !hasChanges(),
                    onClick: onSaveClicked
                }
            }}
        >
            <ListLayout>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 60
                }}>
                    <div className="preview">
                        <img src={addressSlip} alt=""/>
                    </div>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 20
                    }}>
                        {addressSlipCapability?.isAddressSlipForRmaNumberSupported && (
                            <FormFieldLine
                                label="Address slip for RMA #"
                                note={getHint(ADDRESS_SLIP_TRANSLATION_KEYS.ADDRESS_SLIP_FOR_RMA)}
                            >
                                <TranslationInput translationKey={ADDRESS_SLIP_TRANSLATION_KEYS.ADDRESS_SLIP_FOR_RMA}/>
                            </FormFieldLine>
                        )}

                        {addressSlipCapability?.isOrderIdSupported && (
                            <FormFieldLine
                                label="Order ID"
                                note={getHint(ADDRESS_SLIP_TRANSLATION_KEYS.ADDRESS_SLIP_ORDER_ID)}
                            >
                                <TranslationInput translationKey={ADDRESS_SLIP_TRANSLATION_KEYS.ADDRESS_SLIP_ORDER_ID}/>
                            </FormFieldLine>
                        )}

                        {addressSlipCapability?.isOrderIdBarcodeSupported && (
                            <FormFieldLine
                                label="Order ID barcode"
                                note={getHint(ADDRESS_SLIP_TRANSLATION_KEYS.ADDRESS_SLIP_ORDER_ID_BARCODE)}
                            >
                                <TranslationInput translationKey={ADDRESS_SLIP_TRANSLATION_KEYS.ADDRESS_SLIP_ORDER_ID_BARCODE}/>
                            </FormFieldLine>
                        )}

                        {addressSlipCapability?.isWarehouseRmaIdSupported && (
                            <FormFieldLine
                                label="Warehouse RMA ID"
                                note={<>
                                    The RMA ID number for 3PL warehouses to use.&nbsp;
                                    <a rel="noreferrer" target="_blank" href="https://returngo.ai/go/kb/contact-support">Contact support</a> if you prefer to use it instead of the order ID barcode.
                                </>}
                            >
                                <TranslationInput translationKey={ADDRESS_SLIP_TRANSLATION_KEYS.ADDRESS_SLIP_WAREHOUSE_RMA_ID}/>
                            </FormFieldLine>
                        )}

                        {addressSlipCapability?.isWarehouseRmaBarcodeSupported && (
                            <FormFieldLine
                                label="Warehouse RMA barcode"
                                note={getHint(ADDRESS_SLIP_TRANSLATION_KEYS.ADDRESS_SLIP_WAREHOUSE_RMA_ID_BARCODE)}
                            >
                                <TranslationInput translationKey={ADDRESS_SLIP_TRANSLATION_KEYS.ADDRESS_SLIP_WAREHOUSE_RMA_ID_BARCODE}/>
                            </FormFieldLine>
                        )}

                        {addressSlipCapability?.isShipToSupported && (
                            <FormFieldLine
                                label="Ship to"
                                note={getHint(ADDRESS_SLIP_TRANSLATION_KEYS.ADDRESS_SLIP_SHIP_TO)}
                            >
                                <TranslationInput translationKey={ADDRESS_SLIP_TRANSLATION_KEYS.ADDRESS_SLIP_SHIP_TO}/>
                            </FormFieldLine>
                        )}

                        {addressSlipCapability?.isSendBySupported && (
                            <FormFieldLine
                                label="Send by"
                                note={getHint(ADDRESS_SLIP_TRANSLATION_KEYS.ADDRESS_SLIP_SENT_BY)}
                            >
                                <TranslationInput translationKey={ADDRESS_SLIP_TRANSLATION_KEYS.ADDRESS_SLIP_SENT_BY}/>
                            </FormFieldLine>
                        )}

                        {addressSlipCapability?.isAddressSlipInstructionsSupported && (
                            <FormFieldLine
                                label="Address Slip Instructions"
                                note={getHint(ADDRESS_SLIP_TRANSLATION_KEYS.ADDRESS_SLIP_INSTRUCTIONS)}
                            >
                                <TranslationInput translationKey={ADDRESS_SLIP_TRANSLATION_KEYS.ADDRESS_SLIP_INSTRUCTIONS}/>
                            </FormFieldLine>
                        )}
                    </div>
                </div>
            </ListLayout>
        </EditorPageLayout>
        {isShowUnsaved &&
            <ConfirmModal
                cancelButton={{
                    title: 'Cancel',
                    onClick: () => setIsShowUnsaved(false)
                }}
                okButton={{
                    title: 'Discard',
                    onClick: () => {
                        discardChanges();
                        setIsShowUnsaved(false);
                        retryNavigation();
                    }
                }}
            />
        }
    </>
});

export default AddressSlipTranslationsEditorPage;